import React from 'react';

import { useIntl } from 'react-intl';

import ComparisonBadge from 'src/features/home/components/ComparisonBadge';
import { HomeStatistics } from 'src/features/home/types';
import { homeMessages } from 'src/features/home/utils/messages';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import { SectionBlockSubtitle, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import StatisticValue from 'src/registry/components/statistic/StatisticValue';
import { Progress } from 'src/registry/ui/Progress';

type Props = {
    totalOccupancy?: HomeStatistics['total_occupancy'];
    isLoading?: boolean;
    isError?: boolean;
};

const dummyData = [
    {
        title: '-',
        value: 0,
    },
    {
        title: '-',
        value: 0,
    },
    {
        title: '-',
        value: 0,
    },
];
export default function WidgetOccupancy({ totalOccupancy, isLoading, isError }: Props) {
    const { formatMessage } = useIntl();

    const data = React.useMemo(() => {
        return totalOccupancy
            ? [
                  {
                      title: formatMessage(homeMessages.total),
                      value: (totalOccupancy?.currently_occupied_spaces / (totalOccupancy?.total_spaces || 1)) * 100,
                      max: totalOccupancy?.total_spaces,
                  },
                  {
                      title: formatMessage(homeMessages.clients),
                      value:
                          (totalOccupancy?.currently_occupied_client_spaces /
                              (totalOccupancy?.total_client_spaces || 1)) *
                          100,
                      max: totalOccupancy?.total_client_spaces,
                  },
                  {
                      title: formatMessage(homeMessages.members),
                      value:
                          (totalOccupancy?.currently_occupied_membership_spaces /
                              (totalOccupancy?.total_membership_spaces || 1)) *
                          100,
                      max: totalOccupancy?.total_membership_spaces,
                  },
              ]
            : dummyData;
    }, [formatMessage, totalOccupancy]);

    return (
        <GeneralBlock className="flex flex-1 flex-col justify-between">
            <div className="flex flex-col">
                <SectionBlockTitle>{formatMessage(homeMessages.totalOccupancy)}</SectionBlockTitle>
                <SectionBlockSubtitle>{formatMessage(homeMessages.totalOccupancyDescription)}</SectionBlockSubtitle>
            </div>
            {isError ? (
                <GeneralMessageBlock variant="error" />
            ) : (
                <>
                    <div className="flex flex-col gap-2">
                        <div className="flex w-full justify-center gap-5 text-xs">
                            <StatisticValue
                                title={formatMessage(homeMessages.totalSpaces)}
                                value={totalOccupancy?.currently_occupied_spaces}
                                totalValue={totalOccupancy?.total_spaces}
                                isLoading={isLoading}
                            />
                            <StatisticValue
                                title={formatMessage(homeMessages.clientSpaces)}
                                value={totalOccupancy?.currently_occupied_client_spaces}
                                totalValue={totalOccupancy?.total_client_spaces}
                                isLoading={isLoading}
                            />
                            <StatisticValue
                                title={formatMessage(homeMessages.memberSpaces)}
                                value={totalOccupancy?.currently_occupied_membership_spaces}
                                totalValue={totalOccupancy?.total_membership_spaces}
                                isLoading={isLoading}
                            />
                        </div>
                        {data.map((props, index) => (
                            <Progress key={index} {...props} />
                        ))}
                    </div>
                    <ComparisonBadge value={totalOccupancy?.compared_to_last_day} />
                </>
            )}
        </GeneralBlock>
    );
}
